if (!customElements.get('slideshow-hero')) {
  class SlideshowHero extends HTMLElement {
    constructor() {
      super();
      this.currentSlide = 0;
      this.slideCount = 0;
      this.autoRotate = false;
      this.slideDuration = 5000;
      this.isPaused = false;
    }

    connectedCallback() {
      this.slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      this.autoRotate = this.dataset.autoRotate === 'true';
      this.slideDuration = (parseInt(this.dataset.slideDuration, 10) || 5) * 1000;

      this.mediaSlides = [...this.querySelectorAll('.slideshow-hero__slide-media')];
      this.products = [...this.querySelectorAll('.slideshow-hero__product-highlight')];
      this.progressBars = [...this.querySelectorAll('.slideshow-hero__progress-bar')];
      this.contentSlides = [...this.querySelectorAll('.slideshow-hero__slide-content')];
      this.contentStack = this.querySelector('.slideshow-hero__content-stack');
      this.shades = [...this.querySelectorAll('.slideshow-hero__shade')];

      this.wrapper = this.closest('.slideshow-hero-wrapper');
      this.hasParallax = this.wrapper && this.wrapper.hasAttribute('data-has-parallax');
      this.hasScrollMode = this.mediaSlides.some(
        (s) => s.dataset.scrollMode === 'long' || s.dataset.scrollMode === 'parallax'
      );

      // AAS-5234: on an explicit Save-Data request or a 2G-class link, leave the
      // films alone entirely — preload="none" already renders the poster frame,
      // so the slide keeps its composition and simply does not move.
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      this.saveData = !!(conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')));

      if (this.autoRotate && this.slideCount > 1) {
        this.bindProgressEnd();
      }

      // Markup no longer autoplays slide 0 (a display:none twin would autoplay
      // and download too), so playback starts here — after visibleVideo() can
      // tell which twin the breakpoint actually shows.
      this.manageVideos(this.currentSlide);

      // Crossing the breakpoint swaps which twin is rendered. Without this the
      // outgoing twin keeps decoding while hidden and the incoming one sits on
      // its poster, because visibleVideo() is only consulted on slide change.
      // 749px mirrors section-slideshow-new.css:94 — keep the two in step.
      this._mediaQuery = window.matchMedia('(max-width: 749px)');
      this._boundBreakpointChange = () => this.manageVideos(this.currentSlide);
      if (this._mediaQuery.addEventListener) {
        this._mediaQuery.addEventListener('change', this._boundBreakpointChange);
      } else {
        this._mediaQuery.addListener(this._boundBreakpointChange);
      }

      if (this.dataset.pauseOnHover === 'true') {
        this.bindPauseOnHoverTargets();
      }

      this.progressBars.forEach((bar) => {
        const activate = () => {
          const idx = parseInt(bar.dataset.progress, 10);
          if (!isNaN(idx)) {
            this.goToSlide(idx);
            if (this.autoRotate) this.restartProgressAnimation();
          }
        };
        bar.addEventListener('click', activate);
        bar.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate();
          }
        });
      });

      this.contentWrapper = this.querySelector('.slideshow-hero__content-wrapper');

      if (this.slideCount > 1) {
        this.bindSwipe();
      }

      this.products.forEach((product) => this.bindProductCard(product));

      if (this.products.length > 0) {
        this.positionProducts();
        this._boundPositionProducts = () => this.positionProducts();
        window.addEventListener('resize', this._boundPositionProducts);
      }

      if (this.hasParallax || this.hasScrollMode) {
        this.initParallax();
      }

      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect.bind(this));
      }
    }

    // AAS-4076: the flyout used to open/close on raw CSS :hover. Because the
    // card widens AND slides left while open, the hover hit-box is itself the
    // animating geometry — any boundary blip (subpixel hit-test rounding, a
    // slide swap flipping pointer-events under a stationary cursor, fast
    // sweeps across the edge) flipped :hover and made the card oscillate.
    // State is now a JS-owned `.is-open` class with hysteresis: an open-intent
    // delay filters drive-by sweeps, a close grace period absorbs one-frame
    // hover losses, and goToSlide() force-closes every card so slide changes
    // always start from the resting state. `.is-collapsed` is a pure JS latch
    // ("dismissed via the close button, pointer still inside") that blocks
    // reopening until the pointer leaves — it needs no CSS of its own.
    bindProductCard(product) {
      const OPEN_DELAY = 60; // ms of sustained hover before opening (sweep filter)
      // Deliberately lazy: the card survives half a second of hover loss, so
      // casual pointer wandering (including a trip past the browser edge and
      // back) doesn't collapse it. Re-entering within the window cancels the
      // close and the card never moves.
      const CLOSE_GRACE = 500;

      const timers = { open: 0, close: 0 };
      const clearTimers = () => {
        clearTimeout(timers.open);
        clearTimeout(timers.close);
      };

      const open = () => {
        clearTimers();
        if (product.classList.contains('is-collapsed')) return; // dismissed: stay shut until pointer leaves
        if (!product.classList.contains('is-active')) return; // hidden slide (e.g. focus landing on an invisible link)
        product.classList.add('is-open');
      };
      const close = () => {
        clearTimers();
        product.classList.remove('is-open', 'is-collapsed');
        product.dataset.tapped = 'false';
      };

      // goToSlide() reaches for this to reset all cards on slide change
      product._closeCard = close;

      // Touch is excluded from the pointer* pair: a real touch pointerleave
      // fires the instant the finger lifts, which would close the card the
      // same tap just opened. Touch opens via the tap gate below and closes
      // via the emulated mouseleave (fires on tap-away) further down.
      product.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'touch') return;
        clearTimeout(timers.close);
        if (
          product.classList.contains('is-open') ||
          product.classList.contains('is-collapsed')
        ) {
          return;
        }
        clearTimeout(timers.open);
        timers.open = setTimeout(open, OPEN_DELAY);
      });

      product.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'touch') return;
        clearTimers();
        timers.close = setTimeout(close, CLOSE_GRACE);
      });

      // Keyboard: focus into the card opens it immediately (no sweep filter —
      // focus is always deliberate), leaving it closes. :hover never served
      // keyboard users at all.
      product.addEventListener('focusin', open);
      product.addEventListener('focusout', (e) => {
        if (product.contains(e.relatedTarget)) return;
        close();
      });

      const closeBtn = product.querySelector('.slideshow-hero__product-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          clearTimers();
          product.dataset.tapped = 'false';
          product.classList.remove('is-open');
          // Latch until the pointer leaves — pointerleave/mouseleave clear it.
          // Also blocks the focusin above from instantly reopening (the click
          // focuses the button, which sits inside the card).
          product.classList.add('is-collapsed');
        });
      }

      // AAS-744 (mobile): on touch (no-hover) devices require two taps — the
      // first opens the card, the second navigates.
      const link = product.querySelector('.slideshow-hero__product-link');
      if (link) {
        link.addEventListener('click', (e) => {
          if (!window.matchMedia('(hover: none)').matches) return; // pointer devices: navigate
          if (product.dataset.tapped === 'true') return; // already open: let it navigate
          e.preventDefault();
          product.dataset.tapped = 'true';
          open();
        });
      }

      // Touch tap-away close: browsers emulate mouseleave when the user taps
      // outside the card (sticky-hover semantics), which is exactly the
      // dismissal moment for both the two-tap gate and the close latch.
      product.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(hover: none)').matches) return;
        close();
      });
    }

    bindPauseOnHoverTargets() {
      const progress = this.querySelector('.slideshow-hero__progress');
      const ctas = [...this.querySelectorAll('.slideshow-hero__slide-content .btn')];
      const targets = [progress, ...ctas].filter(Boolean);

      targets.forEach((el) => {
        el.addEventListener('mouseenter', this.handleMouseEnter);
        el.addEventListener('mouseleave', this.handleMouseLeave);
        el.addEventListener('focusin', this.handleMouseEnter);
        el.addEventListener('focusout', this.handleMouseLeave);
      });

      this._pauseHoverTargets = targets;
    }

    bindProgressEnd() {
      const progressContainer = this.querySelector('.slideshow-hero__progress');
      if (!progressContainer) return;

      progressContainer.addEventListener('animationend', (e) => {
        if (
          e.target.classList.contains('slideshow-hero__progress-fill') &&
          !this.isPaused
        ) {
          this.nextSlide();
        }
      });
    }

    goToSlide(index) {
      if (index === this.currentSlide && this.slideCount > 1) return;

      this.updateElements(this.mediaSlides, index);
      this.updateElements(this.products, index);
      // Reset every card to its resting state (is-open/is-collapsed off, tap
      // gate re-armed, pending timers cancelled) — the incoming card must
      // never inherit an open state from a cursor that happens to rest on it.
      this.products.forEach((p) => p._closeCard && p._closeCard());
      this.animateContentStack(index);
      this.updateElements(this.shades, index);
      this.updateProgressBars(index);
      this.updateColorMode(index);
      this.manageVideos(index);

      this.currentSlide = index;
    }

    // Toggles the active slide content and smoothly animates the content-stack
    // height between slides. Different slides have different content heights;
    // because the wrapper is bottom-anchored, an instant height change makes
    // the progress bar above it jump in Y. A FLIP on the stack height
    // (old height → new height) lets the progress bar glide instead. Height is
    // reset to auto after the transition so it stays responsive to resize.
    animateContentStack(index) {
      const stack = this.contentStack;
      if (!stack || this.slideCount <= 1) {
        this.updateElements(this.contentSlides, index);
        return;
      }

      const startH = stack.offsetHeight;
      this.updateElements(this.contentSlides, index); // new active sizes the stack
      stack.style.height = 'auto';
      const endH = stack.offsetHeight;

      if (startH === endH) {
        stack.style.height = '';
        return;
      }

      stack.style.height = `${startH}px`;
      void stack.offsetHeight; // reflow to lock the start frame
      stack.style.height = `${endH}px`;

      clearTimeout(this._stackHeightTimer);
      this._stackHeightTimer = setTimeout(() => {
        stack.style.height = '';
      }, 520);
    }

    updateColorMode(activeIndex) {
      const activeContent = this.contentSlides.find(
        (el) => parseInt(el.dataset.slide, 10) === activeIndex
      );
      if (activeContent) {
        this.dataset.colorMode = activeContent.dataset.colorMode || 'dark';
      }
    }

    updateElements(elements, activeIndex) {
      elements.forEach((el) => {
        const elIndex = parseInt(el.dataset.slide, 10);
        el.classList.toggle('is-active', elIndex === activeIndex);
      });
    }

    updateProgressBars(activeIndex) {
      this.progressBars.forEach((bar, i) => {
        bar.classList.remove('is-active', 'is-past');
        if (i < activeIndex) {
          bar.classList.add('is-past');
        } else if (i === activeIndex) {
          bar.classList.add('is-active');
          this.restartFillAnimation(bar);
        }
      });
    }

    restartFillAnimation(bar) {
      const fill = bar.querySelector('.slideshow-hero__progress-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      fill.offsetHeight;
      fill.style.animation = '';
    }

    restartProgressAnimation() {
      const activeBar = this.progressBars[this.currentSlide];
      if (activeBar) this.restartFillAnimation(activeBar);
    }

    // AAS-5234: a slide can carry two <video> twins — the desktop and mobile
    // wrappers switched by display:none in section-slideshow-new.css:78-99.
    // display:none does not stop a <video> downloading, so everything below must
    // act on the ONE twin the current breakpoint renders, never on all of them.
    // offsetParent is null inside a display:none ancestor, which is exactly the
    // question being asked, and it stays correct if the CSS breakpoint moves.
    visibleVideo(slide) {
      const videos = slide.querySelectorAll('video');
      for (const v of videos) {
        if (v.offsetParent !== null) return v;
      }
      return videos[0] || null;
    }

    // Bring a slide's film to the starting line without fetching it outright:
    // preload="metadata" costs a couple hundred KB and lets play() begin without
    // a stall. Only ever called one slide ahead of the rotation, so at most one
    // film is warming while one plays — no matter how many slides carry video.
    warmSlide(index) {
      const slide = this.mediaSlides[index];
      if (!slide) return;
      const v = this.visibleVideo(slide);
      if (!v || v.dataset.warmed === 'true') return;
      v.dataset.warmed = 'true';
      v.preload = 'metadata';
    }

    manageVideos(activeIndex) {
      this.mediaSlides.forEach((slide) => {
        const idx = parseInt(slide.dataset.slide, 10);
        const visible = this.visibleVideo(slide);
        slide.querySelectorAll('video').forEach((v) => {
          if (idx === activeIndex && v === visible) {
            if (this.saveData) return;
            // preload="auto" is set on first play and left on. Without it the
            // browser is free to drop the buffer once the slide rotates away,
            // and play() re-downloads the whole file every time the carousel
            // comes back round — which is how one 4 MB hero film was fetched
            // three times in a single page view.
            v.preload = 'auto';
            v.dataset.warmed = 'true';
            // Safari evaluates the autoplay policy against the muted state at
            // play() time, so assert it here as well as in markup.
            v.muted = true;
            v.play().catch(() => {});
          } else {
            v.pause();
            // readyState 0 means nothing is loaded yet — which is now the resting
            // state of every non-active slide, and seeking one throws.
            if (v.readyState > 0) v.currentTime = 0;
          }
        });
      });

      // Warm the next slide only when the carousel will actually get there, and
      // only once the browser is idle: warming must never compete for bandwidth
      // with the film that is on screen right now.
      if (this.autoRotate && this.slideCount > 1 && !this.saveData) {
        const next = (activeIndex + 1) % this.slideCount;
        const warm = () => this.warmSlide(next);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(warm, { timeout: 2000 });
        } else {
          setTimeout(warm, 1000);
        }
      }
    }

    nextSlide() {
      const next = (this.currentSlide + 1) % this.slideCount;
      this.goToSlide(next);
    }

    prevSlide() {
      const prev = (this.currentSlide - 1 + this.slideCount) % this.slideCount;
      this.goToSlide(prev);
    }

    // AAS-743: touch swipe between slides on mobile. The slides crossfade
    // (no horizontal track), so a swipe past the threshold commits to the
    // existing goToSlide transition. Axis is locked early so vertical
    // scrolling / parallax is never hijacked.
    bindSwipe() {
      const COMMIT = 45; // px of horizontal travel required to change slide
      const AXIS_LOCK = 10; // px of travel before we decide horizontal vs vertical

      let startX = 0;
      let startY = 0;
      let dx = 0;
      let dy = 0;
      let axis = null; // 'x' | 'y' | null
      let active = false;

      const onStart = (e) => {
        if (e.touches.length > 1) return; // ignore pinch / multitouch
        active = true;
        axis = null;
        dx = dy = 0;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      };

      const onMove = (e) => {
        if (!active) return;
        dx = e.touches[0].clientX - startX;
        dy = e.touches[0].clientY - startY;

        if (axis === null && (Math.abs(dx) > AXIS_LOCK || Math.abs(dy) > AXIS_LOCK)) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (axis !== 'x') return; // vertical → let the page scroll

        if (e.cancelable) e.preventDefault(); // own the horizontal gesture
      };

      const onEnd = () => {
        if (!active) return;
        active = false;
        if (axis === 'x' && Math.abs(dx) >= COMMIT) {
          if (dx < 0) this.nextSlide();
          else this.prevSlide();
          if (this.autoRotate) this.restartProgressAnimation();
        }
      };

      this.addEventListener('touchstart', onStart, { passive: true });
      this.addEventListener('touchmove', onMove, { passive: false });
      this.addEventListener('touchend', onEnd);
      this.addEventListener('touchcancel', onEnd);
      this._swipeHandlers = { onStart, onMove, onEnd };
    }

    handleMouseEnter = () => {
      this.isPaused = true;
      const fill = this.querySelector(
        '.slideshow-hero__progress-bar.is-active .slideshow-hero__progress-fill'
      );
      if (fill) fill.style.animationPlayState = 'paused';
    };

    handleMouseLeave = () => {
      this.isPaused = false;
      const fill = this.querySelector(
        '.slideshow-hero__progress-bar.is-active .slideshow-hero__progress-fill'
      );
      if (fill) fill.style.animationPlayState = 'running';
    };

    handleBlockSelect(e) {
      const content = e.target.closest('.slideshow-hero__slide-content');
      if (!content || !this.contains(content)) return;
      const idx = parseInt(content.dataset.slide, 10);
      if (!isNaN(idx)) {
        this.isPaused = true;
        this.goToSlide(idx);
      }
    }

    handleBlockDeselect(e) {
      if (!this.contains(e.target)) return;
      this.isPaused = false;
      if (this.autoRotate && this.slideCount > 1) {
        this.restartProgressAnimation();
      }
    }

    initParallax() {
      this.parallaxData = [];
      this.mediaSlides.forEach((slide) => {
        const mode = slide.dataset.scrollMode;
        if (!mode) return;

        const imgW = parseInt(slide.dataset.imageWidth, 10);
        const imgH = parseInt(slide.dataset.imageHeight, 10);
        const entry = { slide, mode, imgW, imgH, scrollRange: 0 };
        entry.mobileScale = parseInt(slide.dataset.mobileParallaxScale, 10) || 120;

        if (mode === 'parallax') {
          entry.speed = parseFloat(slide.dataset.parallaxSpeed) || 1.5;
          entry.topImgW = parseInt(slide.dataset.topImageWidth, 10);
          entry.topImgH = parseInt(slide.dataset.topImageHeight, 10);
          entry.topScrollRange = 0;
        }

        this.parallaxData.push(entry);
      });

      this.calculateParallaxDimensions();
      this.updateParallaxScroll();

      this._boundParallaxScroll = () => {
        if (!this._parallaxTicking) {
          this._parallaxTicking = true;
          requestAnimationFrame(() => {
            this.updateParallaxScroll();
            this._parallaxTicking = false;
          });
        }
      };
      this._boundParallaxResize = () => this.calculateParallaxDimensions();

      window.addEventListener('scroll', this._boundParallaxScroll, { passive: true });
      window.addEventListener('resize', this._boundParallaxResize);
    }

    calculateParallaxDimensions() {
      if (!this.wrapper) return;

      const vh = window.innerHeight;
      let maxScroll = 0;

      this.parallaxData.forEach((data) => {
        const scale = data.mobileScale / 100;
        const imgH = vh * scale;

        if (data.mode === 'long') {
          data.scrollRange = Math.max(0, imgH - vh);
          data.slide.style.setProperty('--scroll-img-h', `${imgH}px`);
          if (data.scrollRange > maxScroll) maxScroll = data.scrollRange;

        } else if (data.mode === 'parallax') {
          const topH = imgH * data.speed;

          data.scrollRange = Math.max(0, imgH - vh);
          data.topScrollRange = Math.max(0, topH - vh);

          data.slide.style.setProperty('--scroll-img-h', `${imgH}px`);
          data.slide.style.setProperty('--scroll-top-h', `${topH}px`);

          if (data.scrollRange > maxScroll) maxScroll = data.scrollRange;
        }
      });

      this.wrapper.style.setProperty('--parallax-scroll', `${maxScroll}px`);
    }

    updateParallaxScroll() {
      if (!this.wrapper) return;

      const wrapperRect = this.wrapper.getBoundingClientRect();
      const extraScroll = parseFloat(
        getComputedStyle(this.wrapper).getPropertyValue('--parallax-scroll')
      ) || 0;

      if (extraScroll <= 0) return;

      const scrolled = -wrapperRect.top;
      const progress = Math.max(0, Math.min(1, scrolled / extraScroll));

      this.parallaxData.forEach((data) => {
        if (data.mode === 'long') {
          const img = data.slide.querySelector('.slideshow-hero__img');
          if (img && data.scrollRange > 0) {
            img.style.transform = `translateY(${-progress * data.scrollRange}px)`;
          }

        } else if (data.mode === 'parallax') {
          const bottomImg = data.slide.querySelector('.slideshow-hero__img');
          const topImg = data.slide.querySelector('.slideshow-hero__top-img');
          const scrollPx = progress * extraScroll;

          if (bottomImg && data.scrollRange > 0) {
            const bottomOffset = Math.min(scrollPx, data.scrollRange);
            bottomImg.style.transform = `translateY(${-bottomOffset}px)`;
          }

          if (topImg && data.topScrollRange > 0) {
            const topOffset = Math.min(scrollPx * data.speed, data.topScrollRange);
            topImg.style.transform = `translateY(${-topOffset}px)`;
          }
        }
      });

      if (this.autoRotate && this.slideCount > 1) {
        const activeHasScroll = this.parallaxData.some(
          (d) => parseInt(d.slide.dataset.slide, 10) === this.currentSlide
        );
        if (activeHasScroll && progress < 1 && progress > 0) {
          this.pauseAutoRotation();
        } else if (this._parallaxPaused) {
          this.resumeAutoRotation();
        }
      }
    }

    pauseAutoRotation() {
      if (this._parallaxPaused) return;
      this._parallaxPaused = true;
      const fill = this.querySelector(
        '.slideshow-hero__progress-bar.is-active .slideshow-hero__progress-fill'
      );
      if (fill) fill.style.animationPlayState = 'paused';
    }

    resumeAutoRotation() {
      if (!this._parallaxPaused) return;
      this._parallaxPaused = false;
      const fill = this.querySelector(
        '.slideshow-hero__progress-bar.is-active .slideshow-hero__progress-fill'
      );
      if (fill) fill.style.animationPlayState = 'running';
    }

    positionProducts() {
      if (!this.contentWrapper || window.innerWidth >= 750) {
        this.products.forEach((p) => p.style.removeProperty('bottom'));
        return;
      }
      const gap = 10;
      const bottom = this.contentWrapper.offsetHeight + gap;
      this.products.forEach((p) => {
        p.style.bottom = `${bottom}px`;
      });
    }

    disconnectedCallback() {
      if (this._pauseHoverTargets) {
        this._pauseHoverTargets.forEach((el) => {
          el.removeEventListener('mouseenter', this.handleMouseEnter);
          el.removeEventListener('mouseleave', this.handleMouseLeave);
          el.removeEventListener('focusin', this.handleMouseEnter);
          el.removeEventListener('focusout', this.handleMouseLeave);
        });
      }
      if (this._boundParallaxScroll) {
        window.removeEventListener('scroll', this._boundParallaxScroll);
      }
      if (this._boundParallaxResize) {
        window.removeEventListener('resize', this._boundParallaxResize);
      }
      if (this._boundPositionProducts) {
        window.removeEventListener('resize', this._boundPositionProducts);
      }
      if (this._mediaQuery && this._boundBreakpointChange) {
        if (this._mediaQuery.removeEventListener) {
          this._mediaQuery.removeEventListener('change', this._boundBreakpointChange);
        } else {
          this._mediaQuery.removeListener(this._boundBreakpointChange);
        }
      }
      clearTimeout(this._stackHeightTimer);
      if (this._swipeHandlers) {
        this.removeEventListener('touchstart', this._swipeHandlers.onStart);
        this.removeEventListener('touchmove', this._swipeHandlers.onMove);
        this.removeEventListener('touchend', this._swipeHandlers.onEnd);
        this.removeEventListener('touchcancel', this._swipeHandlers.onEnd);
      }
    }
  }

  customElements.define('slideshow-hero', SlideshowHero);
}
